import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ImagePlus, Loader2, Smartphone, Star, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PLACEHOLDER_IMAGE_URL, realImageAssets } from '@/utils/vehicleImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_FILES = 25;
const ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';

function photoIds(list) {
  const used = new Set();
  return (list || []).map((asset, index) => {
    const base = asset?.public_id
      ? `pid:${asset.public_id}`
      : `url:${asset?.url || index}`;
    let id = base;
    let n = 2;
    while (used.has(id)) {
      id = `${base}#${n}`;
      n += 1;
    }
    used.add(id);
    return id;
  });
}

function hapticTick() {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(12);
    }
  } catch {
    /* iOS Safari often ignores vibrate */
  }
}

function SortablePhotoTile({
  id,
  asset,
  index,
  disabled,
  onSetPrimary,
  onRemove,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    touchAction: 'none',
    WebkitUserSelect: 'none',
    userSelect: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group aspect-[4/3] border bg-black/40 overflow-hidden ${
        isDragging ? 'border-blue-400 shadow-lg shadow-black/40 scale-[1.02]' : 'border-white/10'
      }`}
    >
      <button
        type="button"
        className="absolute inset-0 cursor-grab active:cursor-grabbing touch-none"
        aria-label={`Reorder photo ${index + 1}`}
        {...attributes}
        {...listeners}
      >
        <img
          src={asset.url}
          alt={`Vehicle photo ${index + 1}`}
          className="w-full h-full object-cover pointer-events-none"
          draggable={false}
        />
      </button>
      <span className="absolute top-2 right-2 p-1 bg-black/55 text-white/80 pointer-events-none">
        <GripVertical className="w-4 h-4" />
      </span>
      {index === 0 && (
        <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-blue-600 text-white font-['Manrope'] font-semibold pointer-events-none">
          Primary
        </span>
      )}
      <div className="absolute inset-x-0 bottom-0 z-10 p-2 flex items-center justify-end gap-1 bg-gradient-to-t from-black/80 to-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
        {index > 0 && (
          <button
            type="button"
            title="Set as primary"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onSetPrimary(index)}
            className="p-1.5 bg-white/10 hover:bg-blue-600 text-white"
          >
            <Star className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          title="Remove"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onRemove(index)}
          className="p-1.5 bg-white/10 hover:bg-red-600 text-white"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Multi-image uploader for vehicle admin forms.
 * assets: [{ url, public_id? }]
 * onChange(nextAssets)
 */
export default function VehicleImageUploader({
  assets = [],
  onChange,
  token,
  disabled = false,
  imagesPending = false,
  onImagesPendingChange,
}) {
  const inputRef = useRef(null);
  const assetsRef = useRef(Array.isArray(assets) ? assets : []);
  const sessionIdRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrSessionId, setQrSessionId] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrUploadUrl, setQrUploadUrl] = useState('');
  const [qrExpiresAt, setQrExpiresAt] = useState('');
  const [qrPhotoCount, setQrPhotoCount] = useState(0);

  const list = useMemo(() => (Array.isArray(assets) ? assets : []), [assets]);
  assetsRef.current = list;
  const ids = useMemo(() => photoIds(list), [list]);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 280, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const setAssets = useCallback((next) => {
    if (typeof onChange === 'function') onChange(next);
    if (imagesPending && realImageAssets(next).length && typeof onImagesPendingChange === 'function') {
      onImagesPendingChange(false);
    }
  }, [onChange, imagesPending, onImagesPendingChange]);

  const handleDragStart = () => {
    hapticTick();
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
    setAssets(arrayMove(list, oldIndex, newIndex));
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    if (!token) {
      toast.error('Admin session required to upload images');
      return;
    }

    const remaining = MAX_FILES - list.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_FILES} images per vehicle`);
      return;
    }

    const batch = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.message(`Only ${remaining} more image(s) can be added`);
    }

    const formData = new FormData();
    batch.forEach((file) => formData.append('files', file));

    setUploading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/upload/images`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const uploaded = (data.uploads || [])
        .map((u) => ({
          url: u.url,
          public_id: u.public_id || null,
        }))
        .filter((a) => a.url);

      if (!uploaded.length) {
        toast.error('No images were uploaded');
        return;
      }

      setAssets([...list, ...uploaded]);

      if (data.errors?.length) {
        toast.message(`${uploaded.length} uploaded; some files failed`);
      } else {
        toast.success(
          uploaded.length === 1
            ? 'Image uploaded'
            : `${uploaded.length} images uploaded`
        );
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Failed to upload images';
      toast.error(message);
      console.error(error);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (index) => {
    setAssets(list.filter((_, i) => i !== index));
  };

  const closeQrSession = async (sessionId) => {
    const id = sessionId || sessionIdRef.current;
    if (!id || !token) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/photo-sessions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      /* session may already be expired */
    }
  };

  const openMobileQr = async () => {
    if (!token) {
      toast.error('Admin session required');
      return;
    }
    setQrLoading(true);
    setQrOpen(true);
    try {
      if (sessionIdRef.current) {
        await closeQrSession(sessionIdRef.current);
        sessionIdRef.current = null;
        setQrSessionId(null);
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/photo-sessions`,
        { origin: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      sessionIdRef.current = data.session_id;
      setQrSessionId(data.session_id);
      setQrDataUrl(data.qr_data_url || '');
      setQrUploadUrl(data.upload_url || '');
      setQrExpiresAt(data.expires_at || '');
      setQrPhotoCount(0);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create mobile upload QR');
      setQrOpen(false);
      setQrSessionId(null);
    } finally {
      setQrLoading(false);
    }
  };

  const handleCloseQr = async () => {
    const id = sessionIdRef.current;
    setQrOpen(false);
    setQrSessionId(null);
    sessionIdRef.current = null;
    setQrDataUrl('');
    setQrUploadUrl('');
    await closeQrSession(id);
  };

  useEffect(() => {
    if (!qrOpen || !qrSessionId || !token) return undefined;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/photo-sessions/${qrSessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (stopped) return;
        if (!data.open) {
          toast.message('Mobile upload link expired. Generate a new QR to continue.');
          setQrOpen(false);
          setQrSessionId(null);
          sessionIdRef.current = null;
          return;
        }
        const incoming = Array.isArray(data.assets) ? data.assets.filter((a) => a?.url) : [];
        setQrPhotoCount(incoming.length);
        if (incoming.length) {
          const current = assetsRef.current || [];
          const seen = new Set(current.map((a) => a.url));
          const added = incoming.filter((a) => !seen.has(a.url));
          if (added.length) {
            setAssets([...current, ...added]);
            toast.success(
              added.length === 1
                ? 'Photo received from phone'
                : `${added.length} photos received from phone`
            );
          }
        }
      } catch {
        /* keep polling until the admin closes the QR */
      }
    };
    poll();
    const timer = setInterval(poll, 800);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [qrOpen, qrSessionId, token, setAssets]);

  const setPrimary = (index) => {
    if (index <= 0) return;
    const next = [...list];
    const [item] = next.splice(index, 1);
    next.unshift(item);
    setAssets(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <label className="block text-sm font-['Manrope'] font-medium text-white/80">
            Vehicle Photos {imagesPending ? '' : '*'}
          </label>
          <p className="text-xs text-white/40 font-['Manrope'] mt-1">
            Upload up to {MAX_FILES} images (JPEG, PNG, WebP). First image is the primary thumbnail
            customers see. Drag to reorder on desktop. On a phone, press and hold, then drag.
          </p>
          <label className="mt-2 flex items-center gap-2 cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={imagesPending}
              onChange={(e) => {
                if (typeof onImagesPendingChange === 'function') {
                  onImagesPendingChange(e.target.checked);
                }
              }}
              disabled={disabled}
              className="w-4 h-4 bg-white/5 border border-white/10 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
              data-testid="images-pending-checkbox"
            />
            <span className="text-xs font-['Manrope'] text-white/70">
              Yet to acquire Vehicle Images
            </span>
          </label>
        </div>
        <div className="flex flex-col items-stretch gap-2 shrink-0">
        <button
          type="button"
          disabled={disabled || uploading || list.length >= MAX_FILES}
          onClick={openMobileQr}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-colors font-['Manrope'] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Smartphone className="w-4 h-4" />
          Add Photos through Mobile
        </button>
        <button
          type="button"
          disabled={disabled || uploading || list.length >= MAX_FILES}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/40 text-blue-300 hover:bg-blue-500/30 transition-colors font-['Manrope'] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ImagePlus className="w-4 h-4" />
          )}
          {uploading ? 'Uploading…' : 'Upload photos'}
        </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          disabled={disabled || uploading}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {list.length === 0 ? (
        imagesPending ? (
          <div className="w-full border border-dashed border-white/15 bg-white/[0.03] py-6 px-4 text-center">
            <img
              src={PLACEHOLDER_IMAGE_URL}
              alt="Photos not yet acquired"
              className="h-28 mx-auto object-contain mb-3"
            />
            <p className="text-sm text-white/50 font-['Manrope']">
              Placeholder image will be used until photos are uploaded
            </p>
          </div>
        ) : (
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          className="w-full border border-dashed border-white/15 bg-white/[0.03] hover:border-blue-500/40 hover:bg-blue-500/5 transition-colors py-10 px-4 text-center disabled:opacity-50"
        >
          <ImagePlus className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <p className="text-sm text-white/50 font-['Manrope']">
            Click to upload vehicle photos
          </p>
          <p className="text-xs text-white/30 font-['Manrope'] mt-1">
            or drag files here after opening the picker
          </p>
        </button>
        )
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {list.map((asset, index) => (
                <SortablePhotoTile
                  key={ids[index]}
                  id={ids[index]}
                  asset={asset}
                  index={index}
                  disabled={disabled || uploading}
                  onSetPrimary={setPrimary}
                  onRemove={removeAt}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {list.length > 0 && (
        <p className="text-xs text-white/40 font-['Manrope']">
          {list.length} photo{list.length === 1 ? '' : 's'} selected
          {list.length > 1 ? ' · Drag to change customer gallery order' : ''}
          {list.length > 0 ? ' · Star sets primary' : ''}
        </p>
      )}

      {qrOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-sm p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-['Unbounded'] font-bold text-white">Add photos from a phone</h3>
                <p className="text-xs font-['Manrope'] text-white/45 mt-1">
                  Scan this QR. The link works only for this session.
                </p>
              </div>
              <button type="button" onClick={handleCloseQr} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            {qrLoading ? (
              <div className="py-10 flex justify-center text-white/60">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : (
              <>
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="Mobile photo upload QR code" className="w-full bg-white p-3" />
                ) : (
                  <p className="text-sm text-white/50 font-['Manrope']">QR could not be generated.</p>
                )}
                <p className="text-xs font-['Manrope'] text-white/40 mt-3 break-all">{qrUploadUrl}</p>
                <p className="text-sm font-['Manrope'] text-green-300 mt-3">
                  {qrPhotoCount > 0
                    ? `${qrPhotoCount} photo${qrPhotoCount === 1 ? '' : 's'} received — keep this window open`
                    : 'Waiting for photos from the phone… keep this window open'}
                </p>
                {qrExpiresAt && (
                  <p className="text-xs font-['Manrope'] text-white/35 mt-2">
                    Expires {new Date(qrExpiresAt).toLocaleTimeString()}
                  </p>
                )}
                <button
                  type="button"
                  onClick={openMobileQr}
                  className="mt-4 w-full px-3 py-2 border border-white/15 text-white text-sm font-['Manrope'] hover:bg-white/5"
                >
                  Generate new QR
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
